import axios from "axios";
import { setAlert } from "./alert";
import {
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT,
  CLEAR_USER,
  MFA_QR_CODE,
  CLEAR_MFA_ENROLLMENT,
  MFA_ENABLE,
} from "./types";
import setAuthToken from "../utils/setAuthToken";
import { withExpiresFromPayload } from "../utils/sessionAuth";
import customConfig from "../config";
import env from "../env";
// Status Logs column prefs in localStorage — cleared on logout (see deviceStatusColumnUtils.js).
import { clearDeviceStatusColumnPreferences } from "../components/device/DeviceStatus/deviceStatusColumnUtils";

const sortCategories = (list = []) =>
  Array.from(
    new Set(
      (Array.isArray(list) ? list : [])
        .filter(Boolean)
        .map((item) => String(item))
    )
  ).sort((a, b) => a.localeCompare(b));

const getIdpConfig = () => {
  const headers = {
    "Content-Type": "application/json",
    Tenantid: env.GET_TENANT_ID(),
    tenantId: env.GET_TENANT_ID(),
  };
  try {
    const raw = localStorage.getItem("authInfo");
    const authInfo = raw ? JSON.parse(raw) : null;
    const token =
      authInfo?.accessToken ||
      authInfo?.accesstoken ||
      localStorage.getItem("token");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // axios defaults may still carry Authorization
  }
  return { headers };
};

/** Headers for MFA enable/disable — matches Postman: tenantId + Content-Type + Bearer. */
const getMfaToggleConfig = () => {
  const headers = {
    "Content-Type": "application/json",
    tenantId: env.GET_TENANT_ID(),
  };
  try {
    const raw = localStorage.getItem("authInfo");
    const authInfo = raw ? JSON.parse(raw) : null;
    const token = authInfo?.accessToken || authInfo?.accesstoken || localStorage.getItem("token");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // fall through — axios defaults may still have Authorization
  }
  return { headers };
};

const getMfaDevicesUrl = () =>
  `${env.GET_IAM_API_URL()}/api/identity/v1/user/mfa/devices`;

const extractMfaQrCodeUrl = (data = {}) =>
  data.qrCodeUrl || data.qrCode || data.otpAuthUrl || null;

export const clearMfaEnrollment = () => ({ type: CLEAR_MFA_ENROLLMENT });

const extractApiErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  (typeof err?.response?.data === "string" ? err.response.data : null) ||
  fallback;

/** IDP may return accesstoken/refreshtoken (lowercase) or camelCase — normalize for the app. */
const normalizeAuthTokens = (data = {}) => {
  const accessToken = data.accessToken || data.accesstoken || data.AccessToken;
  const refreshToken = data.refreshToken || data.refreshtoken || data.RefreshToken;
  const expiresIn = data.expiresIn ?? data.expiryInSecs ?? data.ExpiryInSecs;
  return {
    ...data,
    accessToken,
    refreshToken,
    expiresIn,
  };
};

/* Login: POST body uses captchaToken + captchaProvider when CAPTCHA is required; tenant comes from axios default X-Tenant-ID (setTenantId). Alerts: 401 uses API message, 403 account locked. */
export const login =
  (dispatch) => async (userName, password, clientId, scope, captchaToken, captchaProvider = "google") => {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const body = { userName, password, clientId };
    if (captchaToken) {
      body.captchaToken = captchaToken;
      body.captchaProvider = captchaProvider;
    }
    try {
      let umURL = env.GET_USERMANAGEMENT_API_URL()
      let authURL =  `${umURL}/simplifi/v1/userManagement/login`

      const res = await axios.post(
        authURL,
        body,
        config
      );

      if (res.data.mfaToken || res.data.tokenId) {
        dispatch({
          type: MFA_ENABLE,
          payload: {
            ...res.data
          }
        })
      }
      return res;
    } catch (err) {
      console.log("This is error : ", err);
      if (!err.response) {
        dispatch(setAlert(customConfig.serverDownMsg, "danger"));
      } else if (err.response.status === 403) {
        dispatch(setAlert(err?.response?.data?.message || "Account is locked. Please contact support.", "danger"));
      } else if (err.response.status === 401) {
        dispatch(setAlert(err?.response?.data?.message || customConfig.authMsg, "danger"));
      } else if (err.response.status === 429) {
        dispatch(setAlert(err?.response?.data?.message || customConfig.authMsg, "danger"));
      } else if ([400, 412, 422].includes(err.response.status)) {
        dispatch(setAlert(err?.response?.data?.message || customConfig.authMsg, "danger"));
      } else if (err.response.status === 500) {
        //added for invalid captcha
        dispatch(setAlert(err?.response?.data?.message || customConfig.authMsg, "danger"));
      } else {
        dispatch(setAlert(customConfig.authMsg, "danger"));
      }
      dispatch({
        type: LOGIN_FAIL,
      });
      throw err;
    }
  };

//LOGOUT
export const logout = () => async (dispatch) => {
  let umURL = env.GET_USERMANAGEMENT_API_URL()
  const clientId = env.GET_CLIENT_ID()

  const config = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  const body = { clientId };
  dispatch({ type: CLEAR_USER });
  dispatch({ type: LOGOUT });
  localStorage.removeItem("token");
  localStorage.removeItem("userInfo");
  localStorage.removeItem("userRoles");
  localStorage.removeItem("userLevel");
  localStorage.removeItem("loggedInUserName");
  localStorage.removeItem("tenantInfo")
  localStorage.removeItem("authInfo")
  clearDeviceStatusColumnPreferences();
  localStorage.removeItem("feedback.visibleColumns");
  try {
    sessionStorage.removeItem("mfaSetupPromptDismissed");
  } catch (e) {
    /* ignore */
  }

  try {
    const res =  await axios.post(
      `${umURL}/simplifi/v1/userManagement/logout`,
      body,
      config
    )
    window.location.reload();
  } catch (err) { }
  setAuthToken();
  if (window && window.location && window.location.reload) {
    window.location.reload();
  } else if (document && document.location && document.location.reload) {
    document.location.reload();
  }
};

/** GET /api/identity/v1/user/mfa/devices — list MFA devices for the logged-in user. */
export const getMfaDevices = () => async (dispatch) => {
  try {
    const res = await axios.get(getMfaDevicesUrl(), getIdpConfig());
    const data = res?.data;
    if (Array.isArray(data)) {
      return data;
    }
    if (Array.isArray(data?.devices)) {
      return data.devices;
    }
    if (data?.deviceName) {
      return [data];
    }
    return [];
  } catch (err) {
    console.error("Get MFA Devices Error", err);
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.getMfaDevicesError),
          "danger"
        )
      );
    }
    throw err;
  }
};

/** POST /api/identity/v1/user/mfa/devices — register a new MFA device and return QR URL. */
export const addMfaDevice = (deviceName) => async (dispatch) => {
  const trimmedName = (deviceName || "").trim();
  if (!trimmedName) {
    dispatch(setAlert(customConfig.mfaDeviceNameRequired, "danger"));
    return null;
  }

  try {
    const res = await axios.post(
      getMfaDevicesUrl(),
      { deviceName: trimmedName },
      getIdpConfig()
    );
    const qrCodeUrl = extractMfaQrCodeUrl(res.data);
    const payload = {
      ...res.data,
      qrCodeUrl,
      deviceName: res.data?.deviceName || trimmedName,
    };
    dispatch({
      type: MFA_QR_CODE,
      payload,
    });
    return payload;
  } catch (err) {
    console.error("MFA Device Error", err);
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else if (err.response.status === 409) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaDeviceNameDuplicate),
          "danger"
        )
      );
    } else if ([400, 412, 422].includes(err.response.status)) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.addMfaDeviceError),
          "danger"
        )
      );
    } else if (err.response.status === 429 || err.response.status === 500) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.addMfaDeviceError),
          "danger"
        )
      );
    } else {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.addMfaDeviceError),
          "danger"
        )
      );
    }
    return null;
  }
};

/**
 * Start MFA enrollment for an existing unverified device using the same UI as Add Device.
 * Removes the stale device (if present), then calls addMfaDevice so MFA_QR_CODE
 * opens the shared QR → Continue → sync screens.
 */
export const prepareMfaDeviceVerification = (deviceName) => async (dispatch) => {
  const trimmedName = (deviceName || "").trim();
  if (!trimmedName) {
    dispatch(setAlert(customConfig.mfaDeviceNameRequired, "danger"));
    return null;
  }

  try {
    await axios.delete(
      `${getMfaDevicesUrl()}/${encodeURIComponent(trimmedName)}`,
      getIdpConfig()
    );
  } catch (err) {
    // Device may already be gone; continue with add so the shared UI still opens.
  }

  return dispatch(addMfaDevice(trimmedName));
};

/** POST /api/identity/v1/user/mfa/devices/{deviceName}/sync — verify device with authenticator codes. */
export const syncMFA = (codes) => async (dispatch, getState) => {
  try {
    const deviceName =
      (codes?.deviceName || getState()?.auth?.mfaDeviceName || "").trim();
    if (!deviceName) {
      dispatch(setAlert(customConfig.mfaDeviceNameRequired, "danger"));
      throw new Error(customConfig.mfaDeviceNameRequired);
    }

    const syncUrl = `${getMfaDevicesUrl()}/${encodeURIComponent(deviceName)}/sync`;
    const body = {
      mfaCode1: codes.code1,
      mfaCode2: codes.code2,
    };

    const res = await axios.post(syncUrl, body, getIdpConfig());

    if (res.status === 200) {
      const skipReload = Boolean(codes?.skipReload);
      const returnPath =
        localStorage.getItem("mfaReturnPath") || "/settings";
      dispatch(
        setAlert(
          skipReload
            ? customConfig.mfaVerifySuccess
            : customConfig.mfaSyncSuccess,
          "success"
        )
      );
      if (!skipReload) {
        localStorage.removeItem("mfaReturnPath");
        setTimeout(() => {
          window.location.href = returnPath;
        }, 3000);
      } else {
        dispatch(clearMfaEnrollment());
      }
    }
    return res;
  } catch (err) {
    if (err?.message === customConfig.mfaDeviceNameRequired && !err.response) {
      throw err;
    }
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else if (err.response.status === 400) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaInvalid),
          "danger"
        )
      );
    } else if (err.response.status === 500) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaSyncError),
          "danger"
        )
      );
    } else {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaSyncError),
          "danger"
        )
      );
    }
    throw err;
  }
};

/** DELETE /api/identity/v1/user/mfa/devices/{deviceName} — remove an MFA device. */
export const removeMfaDevice = (deviceName) => async (dispatch) => {
  const trimmedName = (deviceName || "").trim();
  if (!trimmedName) {
    dispatch(setAlert(customConfig.mfaDeviceNameRequired, "danger"));
    return null;
  }

  try {
    const removeUrl = `${getMfaDevicesUrl()}/${encodeURIComponent(trimmedName)}`;
    const res = await axios.delete(removeUrl, getIdpConfig());
    if (res.status === 200 || res.status === 204) {
      dispatch(setAlert(customConfig.mfaRemoveSuccess, "success"));
    }
    return res;
  } catch (err) {
    console.error("Remove MFA Device Error", err);
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaRemoveError),
          "danger"
        )
      );
    }
    throw err;
  }
};

/**
 * Login MFA OTP validation.
 * POST /api/identity/v1/auth/validateMFA
 * Body: { tokenId, otp }
 */
export const mfaValidate = (otp, mfaToken) => async (dispatch) => {
  try {
    const validateUrl = `${env.GET_IAM_API_URL()}/api/identity/v1/auth/validateMFA`;
    const body = {
      tokenId: mfaToken,
      otp: otp,
    };
    const res = await axios.post(validateUrl, body, getIdpConfig());
    return {
      ...res,
      data: normalizeAuthTokens(res?.data),
    };
  } catch (err) {
    console.log("This is error : ", err);
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else if (err.response.status === 429) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaInvalid),
          "danger"
        )
      );
    } else if (err.response.status === 500) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaInvalid),
          "danger"
        )
      );
    } else {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaInvalid),
          "danger"
        )
      );
    }
    throw err;
  }
};

/** PUT /api/identity/v1/user/mfa/enable — headers + empty object body, same as Postman. */
export const enableMFA = () => async (dispatch) => {
  try {
    const enableUrl = `${env.GET_IAM_API_URL()}/api/identity/v1/user/mfa/enable`;
    const res = await axios.put(enableUrl, {}, getMfaToggleConfig());

    if (res.status === 200) {
      dispatch(
        setAlert(
          res?.data?.message || customConfig.mfaEnableSuccess,
          "success"
        )
      );
    }
    return res;
  } catch (err) {
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaEnableError),
          "danger"
        )
      );
    }
    throw err;
  }
};

/** PUT /api/identity/v1/user/mfa/disable — headers + empty object body, same as Postman. */
export const disableMFA = (options = {}) => async (dispatch) => {
  const silent = Boolean(options?.silent);
  try {
    const disableUrl = `${env.GET_IAM_API_URL()}/api/identity/v1/user/mfa/disable`;
    const res = await axios.put(disableUrl, {}, getMfaToggleConfig());

    if (res.status === 200 && !silent) {
      dispatch(
        setAlert(
          res?.data?.message || customConfig.mfaDisableSuccess,
          "success"
        )
      );
    }
    return res;
  } catch (err) {
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else if (err.response.status === 400) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaInvalid),
          "danger"
        )
      );
    } else if (err.response.status === 500) {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaDisableError),
          "danger"
        )
      );
    } else {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.mfaDisableError),
          "danger"
        )
      );
    }
    throw err;
  }
};

export const validateUserRole = (res) => async (dispatch) => {
  try {
    const authData = normalizeAuthTokens(res?.data);
    const accessToken = authData.accessToken;
    if (!accessToken) {
      dispatch(setAlert(customConfig.authMsg, "danger"));
      dispatch({ type: LOGIN_FAIL });
      throw new Error("Missing access token");
    }

    setAuthToken(accessToken);

    let tokenDetails = null;

    // Prefer UAM tokenDetails when the token is accepted there (non-MFA / legacy login).
    try {
      const umURL = env.GET_USERMANAGEMENT_API_URL();
      const configVal = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const resVal = await axios.get(
        `${umURL}/simplifi/v1/userManagement/tokenDetails`,
        configVal
      );
      tokenDetails = resVal?.data || null;
    } catch (umErr) {
      // IDP MFA tokens are not accepted by UAM — fall back to IDP profile.
      console.warn("UAM tokenDetails failed, falling back to IDP profile", umErr);
    }

    if (!tokenDetails) {
      const iamURL = env.GET_IAM_API_URL();
      const profileRes = await axios.get(
        `${iamURL}/api/identity/v1/user/profile`,
        {
          headers: {
            "Content-Type": "application/json",
            Tenantid: env.GET_TENANT_ID(),
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const profile = profileRes?.data || {};
      tokenDetails = {
        username: profile.username,
        roles: Array.isArray(profile.roles) ? profile.roles : [],
        permissions: Array.isArray(profile.permissions) ? profile.permissions : [],
      };
    }

    // Check for valid roles
    let userLevel = 0;
    let roleList = Array.isArray(tokenDetails.roles) ? tokenDetails.roles : [];
    let loggedInUserRoles = "";

    if (roleList.indexOf(customConfig.roleCustomerSupportL4) > -1) {
      userLevel = 5;
    } else if (
      roleList.indexOf(customConfig.roleCustomerSupportL3Special) > -1
    ) {
      userLevel = 4;
    } else if (roleList.indexOf(customConfig.roleCustomerSupportL3) > -1) {
      userLevel = 3;
    } else if (roleList.indexOf(customConfig.roleCustomerSupportL2) > -1) {
      userLevel = 2;
    } else if (roleList.indexOf(customConfig.roleCustomerSupportL1) > -1) {
      userLevel = 1;
    } else if (roleList.indexOf(customConfig.roleFactoryAdmin) > -1) {
      loggedInUserRoles = customConfig.roleFactoryAdmin;
    }

    let allowCompanyBrand = [];
    const uniqueBrands = new Set();
    let allowedDeviceCategories = [];
    const uniqueCategories = new Set();
    let allowedCountries = [];
    let allowedCountryCodes = [];

    (Array.isArray(tokenDetails.permissions) ? tokenDetails.permissions : []).forEach((element) => {
      if (element.country === "BGD") {
        allowedCountries.push("BD".toLowerCase());
      } else if (element.country?.substring(0, 2) === "SL") {
        allowedCountries.push("LK".toLowerCase());
      } else if (element.country) {
        allowedCountries.push(element.country.toLowerCase());
      }

      if (element?.country && element?.country.toLowerCase() !== "idp") {
        element?.categories?.forEach((category) => {
          uniqueCategories.add(category);
        });
      }

      if (element?.brand && element?.brand.toLowerCase() !== "sys") {
        uniqueBrands.add(element?.brand);
      }

      if (element?.country && allowedCountryCodes.indexOf(element.country) === -1) {
        allowedCountryCodes.push(element.country);
      }
    });

    allowCompanyBrand = [...uniqueBrands];
    allowedDeviceCategories = [...uniqueCategories];
    allowedCountries = allowedCountries.filter(
      (value, index, self) => self.indexOf(value) === index
    );

    if (userLevel > 0 || [customConfig.roleFactoryAdmin].includes(loggedInUserRoles)) {
      const loginPayload = withExpiresFromPayload({
        ...authData,
        ...tokenDetails,
        allowedCountries,
        userRoles: roleList,
        userLevel: userLevel,
        allowedDeviceCategories: sortCategories(allowedDeviceCategories),
        allowCompanyBrand,
        allowedCountryCodes,
      });
      dispatch({
        type: LOGIN_SUCCESS,
        payload: loginPayload,
      });
      localStorage.setItem("authInfo", JSON.stringify(loginPayload));
      setAuthToken(accessToken);
      return res;
    }

    dispatch(setAlert(customConfig.customerSupportRoleErrorMsg, "danger"));
    dispatch({
      type: LOGIN_FAIL,
    });
    throw new Error(customConfig.customerSupportRoleErrorMsg);
  } catch (err) {
    if (err?.message === customConfig.customerSupportRoleErrorMsg) {
      throw err;
    }
    if (!err.response) {
      dispatch(setAlert(customConfig.serverDownMsg, "danger"));
    } else if (err.response.status === 429) {
      dispatch(setAlert(err.response.data.message, "danger"));
    } else if (err.response.status === 500) {
      dispatch(setAlert(err.response.data.message, "danger"));
    } else {
      dispatch(
        setAlert(
          extractApiErrorMessage(err, customConfig.authMsg),
          "danger"
        )
      );
    }
    dispatch({
      type: LOGIN_FAIL,
    });
    throw err;
  }
};
